import { useState, useEffect } from 'react';
import tetrazolioConfiguracoesService from '../../../../services/sementes/tetrazolioConfiguracoes';

export enum GrauSemente {
  GRAU_0 = 0,
  GRAU_1 = 1,
  GRAU_2 = 2,
  GRAU_3 = 3,
  GRAU_4 = 4,
  GRAU_5 = 5,
  GRAU_6 = 6,
  GRAU_7 = 7,
  GRAU_8 = 8,
  GRAU_9 = 9,
}

export enum TipoDano {
  MECANICO = 'mecanico',
  UMIDADE = 'umidade',
  PERCEVEJO = 'percevejo',
  DURAS = 'semente_dura',
  VIGOR = 'vigor',
  VIABILIDADE = 'viabilidade',
  MANCHA_PURPURA = 'mancha_purpura',
  ANORMALIDADE_GENETICA = 'anormalidade_genetica',
  DANO_MECANICO_LATENTE = 'mecanico_latente',
  RASGO_NO_TEGUMENTO = 'rasgo_tegumento',
  ESVERDEADAS = 'esverdeada',
  DANO_MECANICO_IMEDIATO = 'dano_mecanico_imediato',
}

export enum TipoExigenciaDano {
  NENHUM = 'NENHUM',
  NORMAL = 'NORMAL',
  INTENSO = 'INTENSO',
}

export enum IntensidadeDano {
  NORMAL = 'normal',
  INTENSO = 'intenso',
}

export interface GrauConfig {
  id: string;
  grau: GrauSemente;
  cor: string;
  exigenciaDano: TipoExigenciaDano;
  exigeDanoIntenso: boolean;
  isSementeDura: boolean;
  label: string;
  descricao: string;
  valor: number;
}

export interface DanoOpcao {
  tipo: TipoDano;
  label: string;
}

interface UseTetrazolioConfiguracoesResult {
  graus: GrauConfig[];
  danos: DanoOpcao[];
  loading: boolean;
  error: string | null;
}

const GRAU_CORES: Record<number, string> = {
  0: '#FF9500',
  1: '#98C140',
  2: '#FF9500',
  3: '#FF9500',
  4: '#FF9500',
  5: '#FF9500',
  6: '#FF3B30',
  7: '#FF3B30',
  8: '#FF3B30',
  9: '#990800',
};

const GRAU_LABELS: Record<number, string> = {
  0: 'Exige dano',
  1: 'Não usa dano',
  2: 'Exige dano',
  3: 'Exige dano',
  4: 'Exige dano',
  5: 'Exige dano',
  6: 'Exige dano intenso',
  7: 'Exige dano intenso',
  8: 'Exige dano intenso',
  9: 'Semente dura',
};

function useTetrazolioConfiguracoes(): UseTetrazolioConfiguracoesResult {
  const [graus, setGraus] = useState<GrauConfig[]>([]);
  const [danos, setDanos] = useState<DanoOpcao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfiguracoes = async () => {
      try {
        setLoading(true);
        setError(null);

        const [grausResponse, danosResponse] = await Promise.all([
          tetrazolioConfiguracoesService.listGraus(),
          tetrazolioConfiguracoesService.listDanos(),
        ]);

        const grausConfig: GrauConfig[] = grausResponse.graus.map((grau) => {
          const grauNumero = grau.valor;
          const isSementeDura = grauNumero === 9;

          let exigenciaDano: TipoExigenciaDano;
          if (grauNumero === 1) {
            exigenciaDano = TipoExigenciaDano.NENHUM;
          } else if (grau.obrigaDanoIntenso) {
            exigenciaDano = TipoExigenciaDano.INTENSO;
          } else {
            exigenciaDano = TipoExigenciaDano.NORMAL;
          }

          return {
            id: grau.id,
            grau: grauNumero as GrauSemente,
            cor: GRAU_CORES[grauNumero] || '#999999',
            exigenciaDano,
            exigeDanoIntenso: grau.obrigaDanoIntenso,
            isSementeDura,
            descricao: grau.descricao,
            label: GRAU_LABELS[grauNumero] || grau.descricao,
            valor: grau.valor,
          };
        });

        const danosOpcoes: DanoOpcao[] = danosResponse.danos.map((dano) => {
          // Converter string da API para enum TipoDano
          const tipoLower = dano.tipo.toLowerCase();
          const tipoDano = Object.values(TipoDano).find((t) => t === tipoLower)
            || TipoDano.MECANICO;

          return {
            tipo: tipoDano,
            label: dano.label,
          };
        });

        setGraus(grausConfig);
        setDanos(danosOpcoes);
      } catch (err) {
        console.error('Erro ao carregar configurações:', err);
        setError(err instanceof Error ? err.message : 'Erro ao carregar configurações');
      } finally {
        setLoading(false);
      }
    };

    loadConfiguracoes();
  }, []);

  return {
    graus,
    danos,
    loading,
    error,
  };
}

export default useTetrazolioConfiguracoes;
